import { BATCH_DISPLAY_TYPE, BatchResult } from './batch-result';
import { CoordinatorService } from './coordinator.service';
import { XItem } from './x-item';

export class BatchResultItem {
    private _type: BATCH_DISPLAY_TYPE;
    private _value: any;

    constructor(
        private coords: CoordinatorService,
        private parent: BatchResult,
        public itmId: string,
        data: any,
    ) {
        const typeLs = ['doi', 'orcid', 'biblio', 'fulltext'];

        const tp = this.parent.type || this.parent.service;
        if (typeLs.includes(tp)) this._type = tp as BATCH_DISPLAY_TYPE;
        else this._type = data.type;

        if (typeLs.includes(this._type)) this._value = data;
        else this._value = data.value;

        if (this._type === 'biblio') {
            const xitm = this.coords.dataset$.main.get(itmId)!;
            if (xitm) {
                for (const k of Object.keys(this._value)) {
                    let xitmV = xitm.get(k);
                    if (xitmV === undefined) {
                        this.parent.changeSet.add(xitm.id);
                        break;
                    }
                    if (typeof xitmV !== 'string') xitmV = xitmV.toString();

                    const bV = this._value[k];

                    if (xitmV !== bV) {
                        if (bV.endsWith('.') && !xitmV.endsWith('.')) {
                            xitmV += '.';
                            if (bV === xitmV) break;
                        }
                        this.parent.changeSet.add(xitm.id);
                        break;
                    }
                }
            }
        }
    }

    get isOK() {
        return this._value.isOK;
    }

    get hasSuggest() {
        return Array.isArray(this._value) && !!this.value.length;
    }

    get type() {
        return this._type;
    }

    get value() {
        return this._value;
    }

    html() {
        switch (this._type) {
            case 'doi':
                return this._doiHTML();
            case 'orcid':
                return this._orcidHTML();
            case 'fulltext':
                return this._fulltextHTML();
            case 'biblio':
                return this._htmlTable();
            default:
                return this.coords.text(this._value);
        }
    }

    private _fulltextHTML() {
        const ls = this._value;
        if (!Array.isArray(ls) || ls.length === 0) {
            return this.coords.text('No-links-returned');
        }

        ls.sort((a: any, b: any) => b.weight - a.weight);

        const respLs: string[] = [];
        ls.forEach((lnk: any) => {
            let url = lnk.landingURL;
            if (lnk.pdfURL) url = lnk.pdfURL;
            respLs.push(`<div><a href="${url}" target="_blank">${url}</a></div>`);
        });
        return respLs.join('\n');
    }

    private _orcidHTML() {
        if (!this._value.length) {
            return this.coords.text('No-orcid-given');
        }
        const respLs: string[] = [];
        respLs.push(`<table style='margin-bottom: 2ex;'>`);
        respLs.push(
            `<tr><th>${this.coords.text('ORCID')}</th><th>${this.coords.text('Author')}</th>
            <th>${this.coords.text('Works')}</th></tr>`,
        );
        this._value.forEach((itm: any) => {
            const nm = `${itm.name.family} ${itm.name.given}`;
            const orcLnk = `<a href="https://orcid.org/${itm.orcid}" target="_blank">${itm.orcid}</a>`;
            respLs.push(`<tr><td>${orcLnk}</td><td>${nm}</td>
            <td>${this._refsButton(itm.workLs?.length.toString(), 'orcid', itm.orcid)}</td></tr>`);
        });
        respLs.push(`</table>`);
        this.coords.batch$.setTmpStore({
            batchId: this.parent.batchId,
            payload: new Map(this._value.map((x: any) => [x.orcid, x])),
        });
        return respLs.join('');
    }

    private _doiHTML() {
        if (this.isOK) return `OK: ${this.isOK}`;
        if (this.hasSuggest) {
            const ls: string[] = [];
            const currentDOI = this._xitm?.get('doi') || '';
            currentDOI &&
                ls.push(
                    `<div>${this.coords.text('Current')}: <span class="batch-value-different">${currentDOI}</span></div>`,
                );
            this._value.forEach((itm: any) => {
                ls.push("<table style='margin-bottom: 2ex;'>");
                ls.push(`<tr><th>${this.coords.text('DOI')}</th><td>${itm.doi}
                ${this._changeButton('doi', itm.doi)}
                </td></tr>`);
                ls.push(`<tr><th>${this.coords.text('Title')}</th><td>${itm.title}</td></tr>`);
                itm.source &&
                    ls.push(
                        `<tr><th>${this.coords.text('Source')}</th><td>${itm.source}</td></tr>`,
                    );
                itm.volume &&
                    ls.push(
                        `<tr><th>${this.coords.text('Volume')}</th><td>${itm.volume}</td></tr>`,
                    );
                itm.page &&
                    ls.push(`<tr><th>${this.coords.text('Page')}</th><td>${itm.page}</td></tr>`);
                itm.year &&
                    ls.push(`<tr><th>${this.coords.text('Year')}</th><td>${itm.year}</td></tr>`);
                ls.push('</table>');
            });

            return ls.join('');
        }
        return 'DOI';
    }

    private _xitmValue(xitm: XItem, tag: string, batchVal: string) {
        let v = xitm.get(tag) || '';
        if (typeof v !== 'string') v = v.toString();
        v = this.coords.utils$.normalizeSpace(v);
        let isChanged = !v && !!batchVal;

        if (v && batchVal !== v) {
            if (batchVal.endsWith('.') && !v.endsWith('.')) v += '.';
            v = `<span class="batch-value-different">${v}</span>`;
            isChanged = true;
        }
        return [v, isChanged];
    }

    private get _xitm() {
        return this.coords.dataset$.main.get(this.itmId)!;
    }

    private _changeButton(key: string, v: any) {
        const xitemId = this._xitm.id;
        const batchId = this.parent.id;
        const data = { xitemId, batchId, key, v };
        const onClick = `const e = new CustomEvent("xitem-value-change", { detail: ${JSON.stringify(data)} });window.dispatchEvent(e);`;
        return `<button  class="mat-button message-button" onclick='${onClick}'>U</button>`;
    }

    private _refsButton(title: string, key: string, v: any) {
        const xitemId = this._xitm?.id;
        if (!xitemId) return '';
        const batchId = this.parent.batchId;
        const data = { xitemId, batchId, key, v };
        const onClick = `const e = new CustomEvent("references", { detail: ${JSON.stringify(data)} });window.dispatchEvent(e);`;
        return `<button  class="mat-button message-button" onclick='${onClick}'>${title}</button>`;
    }

    private _htmlTable() {
        const xitm = this._xitm;
        if (!xitm) return '';
        const ls: string[] = [];
        ls.push('<table class="batch-table">');
        ls.push(
            `<tr><th></th>
            <th>${this.coords.text(this.parent.service)}</th>
            <th>${this.coords.text('biblio')}</th></tr>`,
        );
        Object.keys(this._value).forEach((key) => {
            let v = this._value[key];

            const [bV, isChanged] = this._xitmValue(xitm, key, v);
            let b = '';
            if (isChanged) b = this._changeButton(key, v);
            switch (key) {
                case 'doi':
                    v = `<a href="https://dx.doi.org/${v}" target="_blank">${v}</a>`;
                    break;
            }

            ls.push(`<tr><th>${this.coords.text(key)}</th>
                <td>${v}${b}</td>
                <td>${bV}</td></tr>`);
        });
        ls.push('</table>');

        return ls.join('\n');
    }
}
